import React from "react";
import { Box } from "@mui/material";
import Section from "../ui/Section";
import Figure from "../ui/Figure";
import Reveal from "../ui/Reveal";
import { Accent, Display, Label } from "../ui/Type";
import { PROCESS } from "../../data/process";
import { color, font, motion, type } from "../../design/tokens";

/**
 * The four stages of a project.
 *
 * Laid out as alternating full-width rows rather than four equal columns: a
 * four-up grid forces every stage to the length of the shortest one, and these
 * stages aren't equally interesting. Each row runs numeral, then a plate, then
 * the copy — with the plate switching sides on alternate rows so the eye
 * zig-zags down the section instead of scanning a column.
 */
const Process = ({ tone = "deep" }) => (
  <Section tone={tone} id="process">
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: { xs: 3, md: 8 },
        alignItems: "end",
        marginBottom: { xs: 6, md: 10 },
      }}
    >
      <Box>
        <Label>How it runs</Label>
        <Display as="h2" size="lg" sx={{ marginTop: 3 }}>
          Four stages, and you'll know which one{" "}
          <Accent>you're in</Accent>.
        </Display>
      </Box>
      <Box component="p" sx={{ ...type.body, color: color.graphite, margin: 0, maxWidth: 440 }}>
        Renovations go wrong in the gaps — between quoting and starting, between
        trades, between "nearly done" and done. These are the four points where
        we make sure nothing is sitting in a gap.
      </Box>
    </Box>

    <Box sx={{ borderTop: `1px solid ${color.line}` }}>
      {PROCESS.map((stage, index) => {
        const flipped = index % 2 === 1;

        return (
          <Reveal key={stage.number} delay={index * 60}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "auto 1fr 1fr" },
                columnGap: { md: 6, lg: 8 },
                rowGap: 3,
                alignItems: "center",
                paddingBlock: { xs: 5, md: 7 },
                borderBottom: `1px solid ${color.line}`,
              }}
            >
              <Box
                component="p"
                aria-hidden="true"
                sx={{
                  fontFamily: font.display,
                  fontSize: { xs: "2.25rem", md: "3.25rem" },
                  lineHeight: 1,
                  color: color.clay,
                  margin: 0,
                  minWidth: { md: 88 },
                }}
              >
                {stage.number}
              </Box>

              <Box sx={{ order: { md: flipped ? 3 : 2 } }}>
                <Display as="h3" size="sm">
                  {stage.title}
                </Display>
                <Box
                  component="p"
                  sx={{ ...type.lead, color: color.ink, marginTop: 1.5, marginBottom: 0 }}
                >
                  {stage.lead}
                </Box>
                <Box component="p" sx={{ ...type.body, color: color.graphite, marginTop: 2 }}>
                  {stage.body}
                </Box>
              </Box>

              <Box
                sx={{
                  order: { md: flipped ? 2 : 3 },
                  maxWidth: { xs: "100%", md: 320 },
                  marginLeft: { md: flipped ? 0 : "auto" },
                  width: "100%",
                }}
              >
                <Figure
                  media={stage.photo}
                  ratio="5 / 4"
                  caption={false}
                  sx={{
                    transition: `filter ${motion.slow} ${motion.ease}`,
                  }}
                />
              </Box>
            </Box>
          </Reveal>
        );
      })}
    </Box>
  </Section>
);

export default Process;
